import { useIntl } from "react-intl";

import { BillingModalShell } from "./billing-modal-shell";
import { type BillingPlanId } from "./billing-state";
import { Button, cn } from "@runcheetah/ui";

type PlanOption = {
  id: BillingPlanId;
  yearlyMonthly: number | null;
  featured?: boolean;
  inheritsFromMessageId?: string;
  featureMessageIds: string[];
};

const PLAN_OPTIONS: PlanOption[] = [
  {
    id: "starter",
    yearlyMonthly: 55,
    featureMessageIds: [
      "brandSettings.billing.planStarterFeature1",
      "brandSettings.billing.planStarterFeature2",
      "brandSettings.billing.planStarterFeature3",
      "brandSettings.billing.planStarterFeature4",
    ],
  },
  {
    id: "growth",
    yearlyMonthly: 119,
    featured: true,
    inheritsFromMessageId: "brandSettings.billing.planStarterName",
    featureMessageIds: [
      "brandSettings.billing.planGrowthFeature1",
      "brandSettings.billing.planGrowthFeature2",
    ],
  },
  {
    id: "pro",
    yearlyMonthly: null,
    featureMessageIds: [
      "brandSettings.billing.planProFeature1",
      "brandSettings.billing.planProFeature2",
      "brandSettings.billing.planProFeature3",
      "brandSettings.billing.planProFeature4",
      "brandSettings.billing.planProFeature5",
    ],
  },
];

const getPlanCopy = (intl: ReturnType<typeof useIntl>, planId: BillingPlanId) => {
  if (planId === "starter") {
    return {
      name: intl.formatMessage({
        id: "brandSettings.billing.planStarterName",
        defaultMessage: "Starter",
      }),
      tagline: intl.formatMessage({
        id: "brandSettings.billing.planStarterTagline",
        defaultMessage: "Mostly used by small businesses and solo creators.",
      }),
      ctaLabel: intl.formatMessage({
        id: "brandSettings.billing.planGetStarted",
        defaultMessage: "Get started",
      }),
    };
  }

  if (planId === "growth") {
    return {
      name: intl.formatMessage({
        id: "brandSettings.billing.planGrowthName",
        defaultMessage: "Growth",
      }),
      tagline: intl.formatMessage({
        id: "brandSettings.billing.planGrowthTagline",
        defaultMessage: "Mostly used by freelance marketers managing multiple clients.",
      }),
      ctaLabel: intl.formatMessage({
        id: "brandSettings.billing.planGetStarted",
        defaultMessage: "Get started",
      }),
    };
  }

  return {
    name: intl.formatMessage({
      id: "brandSettings.billing.planProName",
      defaultMessage: "Pro",
    }),
    tagline: intl.formatMessage({
      id: "brandSettings.billing.planProTagline",
      defaultMessage: "Mostly used by agencies and businesses that want full-service management.",
    }),
    ctaLabel: intl.formatMessage({
      id: "brandSettings.billing.planContactUs",
      defaultMessage: "Contact us",
    }),
  };
};

export function BillingPlanModal({
  currentPlan,
  title,
  description,
  onClose,
  onPlanPick,
}: {
  currentPlan?: BillingPlanId | null;
  title: string;
  description: string;
  onClose: () => void;
  onPlanPick: (planId: BillingPlanId) => void;
}) {
  const intl = useIntl();

  return (
    <BillingModalShell
      title={title}
      description={description}
      onClose={onClose}
      widthClassName="max-w-[980px]"
    >
      <div className="grid gap-[14px] lg:grid-cols-3">
        {PLAN_OPTIONS.map((plan) => {
          const isCurrent = currentPlan ? plan.id === currentPlan : false;
          const isFeatured = Boolean(plan.featured);
          const copy = getPlanCopy(intl, plan.id);

          return (
            <div
              key={plan.id}
              className={cn(
                "relative flex flex-col gap-[14px] rounded-[14px] border p-5",
                isFeatured
                  ? "border-[#0D1F2D] bg-[#0D1F2D] text-white"
                  : "border-[rgba(13,31,45,.08)] bg-white text-[#0D1F2D]",
              )}
            >
              {isFeatured ? (
                <span className="absolute -top-[10px] right-4 rounded-full bg-[#C8FF00] px-[9px] py-[3px] text-[10px] font-extrabold uppercase tracking-[0.05em] text-[#0D1F2D]">
                  {intl.formatMessage({
                    id: "brandSettings.billing.mostPopular",
                    defaultMessage: "Most popular",
                  })}
                </span>
              ) : null}

              <div>
                <div className="text-sm font-extrabold tracking-[-0.01em]">{copy.name}</div>
                <div
                  className={cn(
                    "mt-0.5 text-[11px] leading-[1.4]",
                    isFeatured ? "text-white/65" : "text-[rgba(13,31,45,.6)]",
                  )}
                >
                  {copy.tagline}
                </div>
              </div>

              <div className="flex flex-col gap-0.5">
                {plan.yearlyMonthly === null ? (
                  <span className="text-[22px] font-extrabold tracking-[-0.02em]">
                    {intl.formatMessage({
                      id: "brandSettings.billing.planCustom",
                      defaultMessage: "Custom",
                    })}
                  </span>
                ) : (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="text-[28px] font-extrabold tracking-[-0.02em]">
                        {intl.formatMessage(
                          {
                            id: "brandSettings.billing.planMonthlyPrice",
                            defaultMessage: "€{amount}",
                          },
                          { amount: plan.yearlyMonthly },
                        )}
                      </span>
                      <span
                        className={cn(
                          "text-[11px]",
                          isFeatured ? "text-white/65" : "text-[rgba(13,31,45,.6)]",
                        )}
                      >
                        {intl.formatMessage({
                          id: "brandSettings.billing.planPerMonth",
                          defaultMessage: "/mo",
                        })}
                      </span>
                    </div>
                    <div
                      className={cn(
                        "text-[11px]",
                        isFeatured ? "text-white/55" : "text-[rgba(13,31,45,.6)]",
                      )}
                    >
                      {intl.formatMessage({
                        id: "brandSettings.billing.planBilledYearly",
                        defaultMessage: "Billed yearly",
                      })}
                    </div>
                  </>
                )}
              </div>

              {plan.inheritsFromMessageId ? (
                <div
                  className={cn(
                    "border-b pb-2 text-[11px] font-bold",
                    isFeatured
                      ? "border-white/12 text-white/85"
                      : "border-[rgba(13,31,45,.08)] text-[#0D1F2D]",
                  )}
                >
                  {intl.formatMessage(
                    {
                      id: "brandSettings.billing.planInheritsFrom",
                      defaultMessage: "Everything in {planName}, plus:",
                    },
                    {
                      planName: intl.formatMessage({
                        id: plan.inheritsFromMessageId,
                        defaultMessage: "Starter",
                      }),
                    },
                  )}
                </div>
              ) : null}

              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {plan.featureMessageIds.map((featureMessageId) => (
                  <li
                    key={featureMessageId}
                    className="flex items-start gap-[7px] text-xs leading-[1.4]"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "mt-px font-extrabold",
                        isFeatured ? "text-[#C8FF00]" : "text-[#7A9900]",
                      )}
                    >
                      ✓
                    </span>
                    <span className={cn(isFeatured ? "text-white/92" : "text-[#0D1F2D]")}>
                      {intl.formatMessage({
                        id: featureMessageId,
                        defaultMessage: featureMessageId,
                      })}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                type="button"
                onClick={() => onPlanPick(plan.id)}
                className={cn(
                  "mt-auto rounded-[10px] px-[14px] py-2.5 text-[13px] font-extrabold tracking-[-0.01em]",
                  isFeatured
                    ? "bg-[#C8FF00] text-[#0D1F2D] hover:bg-[#C8FF00]/90"
                    : "bg-[#0D1F2D] text-[#C8FF00] hover:bg-[#0D1F2D]/95",
                )}
              >
                {isCurrent
                  ? intl.formatMessage({
                      id: "brandSettings.billing.planCurrent",
                      defaultMessage: "Current plan",
                    })
                  : copy.ctaLabel}
              </Button>
            </div>
          );
        })}
      </div>
    </BillingModalShell>
  );
}
